/**
 * Difference mot a mot de deux textes (« Comparer a aujourd'hui » d'une version precedente, vue partagee des
 * differences de GitHub, historique des versions de Google Docs) : dans une note longue, dire QUELS mots ont ete
 * retires et ajoutes, au lieu de marquer tout le champ comme « different ».
 *
 * Pur, zero E/S, aucune valeur gardee : les deux textes sont lus au rendu et le resultat n'est que des morceaux de ces
 * memes textes. Ce n'est pas de la crypto ; c'est la plus longue sous-suite commune classique (programmation
 * dynamique), sur des jetons -- mots, blancs, ponctuation un caractere a la fois.
 *
 * Invariant (teste) : les morceaux `same` + `removed` mis bout a bout redonnent EXACTEMENT le texte d'avant, et les
 * morceaux `same` + `added` celui d'apres -- une difference ne perd, n'invente ni ne deplace jamais un caractere.
 *
 * Borne de cout : la table est limitee a `MAX_DIFF_CELLS` cases. Au-dela, la comparaison se refait ligne a ligne ; si
 * c'est encore trop, le milieu different est rendu d'un bloc (retire puis ajoute) -- grossier, jamais faux.
 */

export interface DiffSegment {
  readonly kind: 'same' | 'removed' | 'added';
  readonly text: string;
}

/** Cases au plus dans la table de la plus longue sous-suite commune (4 octets chacune : 8 Mo). */
export const MAX_DIFF_CELLS = 2_000_000;

/** Mot (lettres, chiffres, soulignes, accents compris), suite de blancs, ou un autre caractere seul. */
const WORD_TOKENS = /[\p{L}\p{N}\p{M}_]+|\s+|[^\s\p{L}\p{N}\p{M}_]/gu;

function wordTokens(text: string): string[] {
  return text.match(WORD_TOKENS) ?? [];
}

/** Lignes, fin de ligne comprise (`\r\n`, `\n` ou `\r` seul). */
function lineTokens(text: string): string[] {
  return text.match(/[^\r\n]*(?:\r\n|\n|\r)|[^\r\n]+$/g) ?? [];
}

/** Plus longue sous-suite commune de deux listes de jetons ; `null` si la table depasse la borne. */
function lcsSegments(a: readonly string[], b: readonly string[]): DiffSegment[] | null {
  const n = a.length;
  const m = b.length;
  if ((n + 1) * (m + 1) > MAX_DIFF_CELLS) return null;
  const width = m + 1;
  // table[i][j] = longueur de la plus longue sous-suite commune de a[i..] et b[j..].
  const table = new Uint32Array((n + 1) * width);
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      table[i * width + j] = a[i] === b[j]
        ? table[(i + 1) * width + j + 1]! + 1
        : Math.max(table[(i + 1) * width + j]!, table[i * width + j + 1]!);
    }
  }
  const segments: DiffSegment[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      segments.push({ kind: 'same', text: a[i]! });
      i += 1;
      j += 1;
    } else if (table[(i + 1) * width + j]! >= table[i * width + j + 1]!) {
      segments.push({ kind: 'removed', text: a[i]! });
      i += 1;
    } else {
      segments.push({ kind: 'added', text: b[j]! });
      j += 1;
    }
  }
  for (; i < n; i += 1) segments.push({ kind: 'removed', text: a[i]! });
  for (; j < m; j += 1) segments.push({ kind: 'added', text: b[j]! });
  return segments;
}

/** Jetons communs en tete et en queue retires avant la table : une note longue peu changee reste bon marche. */
function diffTokens(a: readonly string[], b: readonly string[]): DiffSegment[] | null {
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start += 1;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA -= 1;
    endB -= 1;
  }
  const middle = lcsSegments(a.slice(start, endA), b.slice(start, endB));
  if (middle === null) return null;
  return [
    ...a.slice(0, start).map((text): DiffSegment => ({ kind: 'same', text })),
    ...middle,
    ...a.slice(endA).map((text): DiffSegment => ({ kind: 'same', text })),
  ];
}

/**
 * Regroupe : un blanc ou une ponctuation commune (sans lettre ni chiffre) pris entre deux changements est rattache
 * aux changements (« bravo-2222 » / « charlie-3333 » plutot qu'une mosaique mot / tiret / mot) ; dans chaque bloc change, tout le retire precede tout l'ajoute ;
 * les morceaux voisins de meme nature sont fusionnes.
 */
function tidy(segments: readonly DiffSegment[]): DiffSegment[] {
  const absorbed = segments.map((segment, index): DiffSegment | null => {
    if (segment.kind !== 'same' || /[\p{L}\p{N}]/u.test(segment.text)) return segment;
    const previous = segments[index - 1];
    const next = segments[index + 1];
    return previous !== undefined && next !== undefined && previous.kind !== 'same' && next.kind !== 'same' ? null : segment;
  });
  const result: DiffSegment[] = [];
  let removed = '';
  let added = '';
  const flush = (): void => {
    if (removed.length > 0) result.push({ kind: 'removed', text: removed });
    if (added.length > 0) result.push({ kind: 'added', text: added });
    removed = '';
    added = '';
  };
  segments.forEach((segment, index) => {
    if (absorbed[index] === null) {
      // Blanc ou ponctuation commune rattachee : present des deux cotes, il compte pour le retire comme pour l'ajoute.
      removed += segment.text;
      added += segment.text;
    } else if (segment.kind === 'removed') {
      removed += segment.text;
    } else if (segment.kind === 'added') {
      added += segment.text;
    } else {
      flush();
      const last = result[result.length - 1];
      if (last?.kind === 'same') result[result.length - 1] = { kind: 'same', text: last.text + segment.text };
      else result.push(segment);
    }
  });
  flush();
  return result;
}

/** Qualite d'une frontiere entre deux caracteres : 2 en bord de texte ou de ligne, 1 entre mot et blanc ou ponctuation. */
function boundaryScore(left: string | undefined, right: string | undefined): number {
  if (left === undefined || right === undefined || /[\r\n]/.test(left) || /[\r\n]/.test(right)) return 2;
  return /[\p{L}\p{N}\p{M}_]/u.test(left) && /[\p{L}\p{N}\p{M}_]/u.test(right) ? 0 : 1;
}

/**
 * Glisse un ajout (ou un retrait) seul entre deux morceaux communs vers sa meilleure frontiere, sans rien changer aux
 * textes (le « cleanupSemanticLossless » de diff-match-patch) : « samedi[.\nCarte a renouveler]. » devient
 * « samedi.[\nCarte a renouveler.] » -- le bloc commence a la fin de ligne, comme on l'a ecrit.
 */
function slide(segments: DiffSegment[]): DiffSegment[] {
  for (let index = 0; index < segments.length; index += 1) {
    const change = segments[index]!;
    const previous = segments[index - 1];
    const next = segments[index + 1];
    if (change.kind === 'same' || (previous !== undefined && previous.kind !== 'same') || (next !== undefined && next.kind !== 'same')) continue;
    let left = previous?.text ?? '';
    let middle = change.text;
    let right = next?.text ?? '';
    // Tout a gauche, puis chaque position vers la droite ; la premiere meilleure l'emporte.
    while (left.length > 0 && left[left.length - 1] === middle[middle.length - 1]) {
      right = middle[middle.length - 1] + right;
      middle = left[left.length - 1] + middle.slice(0, -1);
      left = left.slice(0, -1);
    }
    let best = { left, middle, right, score: -1 };
    for (;;) {
      // Jamais entre les deux moities d'un caractere hors du plan de base (emoji) : il s'afficherait casse.
      const split = /[\uD800-\uDBFF]$/.test(left) || /[\uD800-\uDBFF]$/.test(middle);
      const score = boundaryScore(left[left.length - 1], middle[0]) + boundaryScore(middle[middle.length - 1], right[0]);
      if (!split && score > best.score) best = { left, middle, right, score };
      if (right.length === 0 || right[0] !== middle[0]) break;
      left += middle[0];
      middle = middle.slice(1) + right[0];
      right = right.slice(1);
    }
    if (previous !== undefined) segments[index - 1] = { kind: 'same', text: best.left };
    segments[index] = { kind: change.kind, text: best.middle };
    if (next !== undefined) segments[index + 1] = { kind: 'same', text: best.right };
  }
  return segments.filter((segment) => segment.text.length > 0);
}

/** Difference mot a mot de `before` a `after` (voir l'en-tete du module). */
export function diffWords(before: string, after: string): DiffSegment[] {
  if (before === after) return before.length === 0 ? [] : [{ kind: 'same', text: before }];
  const words = diffTokens(wordTokens(before), wordTokens(after));
  if (words !== null) return slide(tidy(words));
  const lines = diffTokens(lineTokens(before), lineTokens(after));
  if (lines !== null) return slide(tidy(lines));
  // Trop long meme ligne a ligne : le milieu different d'un bloc, tete et queue communes gardees.
  let start = 0;
  while (start < before.length && start < after.length && before[start] === after[start]) start += 1;
  let end = 0;
  while (end < before.length - start && end < after.length - start && before[before.length - 1 - end] === after[after.length - 1 - end]) end += 1;
  return tidy([
    { kind: 'same', text: before.slice(0, start) },
    { kind: 'removed', text: before.slice(start, before.length - end) },
    { kind: 'added', text: after.slice(start, after.length - end) },
    { kind: 'same', text: before.slice(before.length - end) },
  ].filter((segment) => segment.text.length > 0) as DiffSegment[]);
}

/** Ce que la difference retire et ajoute, en caracteres non blancs (le resume d'un champ : « 12 retires, 30 ajoutes »). */
export function diffCounts(segments: readonly DiffSegment[]): { removed: number; added: number } {
  const visible = (text: string): number => text.replace(/\s+/g, '').length;
  let removed = 0;
  let added = 0;
  for (const segment of segments) {
    if (segment.kind === 'removed') removed += visible(segment.text);
    else if (segment.kind === 'added') added += visible(segment.text);
  }
  return { removed, added };
}
